import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PASSWORD_RULES, passwordStrengthValidator } from '@yol-digital/yol-regex';
import { map } from 'rxjs';
import { BrandService } from 'brand';
import { SvgComponent } from 'icon';
import { TranslatePipe } from 'translate';
import { FormFieldComponent } from '../input-form-field/input-form-field.component';
import { matchValidator } from '../validators/match.validator';

@Component({
  selector: 'lib-password-strength-form-field',
  imports: [CommonModule, TranslatePipe, FormFieldComponent, ReactiveFormsModule, SvgComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './password-strength-form-field.component.html',
})
export class PasswordStrengthFormFieldComponent {
  public brand = inject(BrandService).brand;
  public passwordForm = new FormGroup(
    {
      password: new FormControl('', [control => passwordStrengthValidator(control.value)]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: [matchValidator('password', 'confirmPassword')] }
  );
  public passwordRules = PASSWORD_RULES;
  public errorsObj = toSignal(this.passwordControl.statusChanges.pipe(map(() => this.passwordControl.errors)));
  public progressValue = computed(() => {
    const errors = this.errorsObj();
    if (errors === undefined) return 0;
    const total = PASSWORD_RULES.length;
    const errorsLength = errors ? Object.keys(errors).length : 0;
    const progress = (100 * (total - errorsLength)) / total;

    return progress;
  });
  public colorClass = computed(() => {
    if (this.progressValue() < 50) return 'bg-gradient-to-r from-[--color-border-negative] to-[--color-border-warning]';
    if (this.progressValue() < 67) return 'bg-gradient-to-r from-[--color-border-warning] to-[--color-border-positive]';
    return 'bg-[--color-border-positive]';
  });
  public progressWidthStyle = computed(() => `width: ${this.progressValue()}%;`);
  public strengthMessage = computed(() => {
    if (this.progressValue() === 0) return 'strength_requirements';
    if (this.progressValue() < 50) return 'strength_weak';
    if (this.progressValue() < 100) return 'strength_fair';
    return 'strength_strong';
  });
  public disabled = input<boolean>(false);
  public resetForm = input<boolean>(false);
  public autoFocus = input<boolean>(true);
  public passwordValue = output<string>();

  constructor() {
    effect(() => {
      if (this.resetForm()) {
        this.reset();
      }
    });
    effect(() => {
      if (this.errorsObj() === null) {
        this.confirmPasswordControl.markAsTouched();
        this.confirmPasswordControl.updateValueAndValidity();
      }
    });
    this.passwordForm.statusChanges.subscribe(status => {
      if (status == 'VALID') this.passwordValue.emit(this.passwordControl.value);
      else this.passwordValue.emit(null);
    });
  }

  public get passwordControl() {
    return this.passwordForm?.get('password');
  }

  public get confirmPasswordControl() {
    return this.passwordForm?.get('confirmPassword');
  }

  public reset() {
    this.passwordForm.reset({ password: '', confirmPassword: '' });
  }
}
